import { SentimentIcon } from "./SentimentIcon";
import { Formatter } from "./format";
import {Tag} from "./Tag";
import { Box } from '@cloudscape-design/components';
import "../AiPage.scss";


function timeToSeconds(timeStr) {
  const [hours, minutes, seconds] = timeStr.split(':').map(Number);
  return hours * 3600 + minutes * 60 + seconds;
}
const getTranscriptDetails = (allSegments) => {
  let textSoFar = '';
  return allSegments.map(item => {
    const segmentText = item?.Text || '';
    const offsetStart = textSoFar.length;
    const offsetEnd = offsetStart + segmentText.length;
    
    const result = {
      content: segmentText,
      start: item.StartTime,
      end: item.EndTime,
      confidence: item.Confidence,
      offsetStart,
      offsetEnd
    };
    
    textSoFar += segmentText;
    return result;
  });
}

const generateChatElement = (text, allSegments, highlightLocations, onClick, timeMoment) => {
  if (!allSegments.length) {
    return applyReplacements(text, highlightLocations);
  }
  
  const transcript = getTranscriptDetails(allSegments);
  const segmentText = transcript.map(s => s.content).join('');
  
  // Find newline positions in original text
  const newlinePositions = [];
  for (let i = 0; i < text.length; i++) {
    if (text[i] === '\n') {
      newlinePositions.push(i);
    }
  }
  
  const result = [];
  let segmentIndex = 0;
  let textPos = 0;
  
  transcript.forEach((segment, i) => {
    // Check if we need to insert newlines before this segment
    while (newlinePositions.length > 0 && newlinePositions[0] <= textPos) {
      result.push(<br key={`br-${newlinePositions[0]}`} />);
      newlinePositions.shift();
    }
    
    // Add the segment
    const addHighlight = highlightLocations.filter(highlight => 
      highlight.start === segment.offsetStart || highlight.end === segment.offsetEnd || 
      (highlight.start <= segment.offsetStart && highlight.end >= segment.offsetEnd) || 
      (highlight.end >= segment.offsetStart && highlight.end <= segment.offsetEnd)
    );
    
    let textStyle = {};
    let tooltip = "";
    
    if (segment.confidence < 0.5) {
      textStyle = { color: "red", opacity: 0.9 };
      tooltip = `Very low confidence: ${(segment.confidence * 100).toFixed(0)}%`;
    } else if (segment.confidence < 0.7) {
      textStyle = { color: "orange", textDecoration: "underline dotted" };
      tooltip = `Low confidence: ${(segment.confidence * 100).toFixed(0)}%`;
    } else if (segment.confidence < 0.9) {
      textStyle = { fontStyle: "italic", color: "purple", opacity: 0.6 };
      tooltip = `Medium confidence: ${(segment.confidence * 100).toFixed(0)}%`;
    }
    
    const addFlag = parseFloat(timeToSeconds(timeMoment)) >= parseFloat(segment.start) &&
                   parseFloat(timeToSeconds(timeMoment)) <= parseFloat(segment.end);
    
    const key = "segment-" + segment.offsetStart;
    
    if (addHighlight.length) {
      result.push(
        <span key={key}>
          {addFlag && <span style={{ height: '20px', minWidth: '5px', background: 'rgb(250, 100, 73)', display: "inline-block", marginRight: '4px', fontSize: "10px", padding: '0px', color: 'white', borderRadius: '6px' }}>moment</span>}
          <span onClick={onClick} className="word_conversation">
            {addHighlight[0].fn(segment.content, key, segment.start, segment.end, segment.offsetStart, segment.offsetEnd)}
          </span>
        </span>
      );
    } else {
      result.push(
        <span
          key={key}
          onClick={onClick}
          data-start={segment.start}
          data-end={segment.end}
          data-offset-start={segment.offsetStart}
          data-offset-end={segment.offsetEnd}
          style={{ cursor: 'pointer', ...textStyle }}
          className="word_conversation"
          title={tooltip || undefined}
        >
          {addFlag && <span style={{ height: '20px', minWidth: '5px', background: 'rgb(250, 100, 73)', display: "inline-block", marginRight: '4px', fontSize: "10px", padding: '0px 10px', color: 'white', borderRadius: '6px' }}>moment</span>}
          {segment.content.split('\n').map((line, lineIndex) => 
            lineIndex === 0 ? line : [<br key={`${i}-${lineIndex}`} />, line]
          ).flat()}
        </span>
      );
    }
    
    textPos += segment.content.length;
  });
  
  return result;

}

export const ChatSegment = ({
  name,
  allSegments,
  segmentStart,
  text,
  onClick,
  highlightLocations,
  score,
  interruption,
  ivr,
  categoryList,
  timeMoment
}) => (
  <div style={{display:"flex", paddingBottom:"1rem"}}>
    <div style={{margin: "0.4rem"}}>
      <SentimentIcon score={score} size="2em" />
    </div>
    <div>
    <span className={"text-muted segment"}>
      {name}
      {segmentStart && segmentStart > 1735689600 && (
        <>
          {" - "}
          <span className="audio-start" data-currenttime={segmentStart}>
            {new Date(segmentStart*1000).toLocaleString("en-US", {
              month: "numeric",
              day: "numeric",
              hour: "numeric",
              minute: "2-digit",
              hour12: true,
            }).replace(",", "")}
          </span>
        </>
      )}
    </span>
      {ivr && (
        <Tag key="ivr" bg="danger" className="ms-2">
          IVR
        </Tag>
      )}
      {interruption && (
        <Tag key="warning" bg="warning" text="dark" className="ms-2">
          Interruption
        </Tag>
      )}
      {categoryList.length > 0 && (
        categoryList.map((category, j) => (
              <Tag key={j} bg="primary" className="ms-2">
                {category}
              </Tag>
            )
          )
        )}
      <div>{generateChatElement(text, allSegments, highlightLocations, onClick, timeMoment)}</div>
    </div>
  </div>
);

// substituteAt replaces a subset of a string with the value of the fn provided.
// it returns an array containing string and react elements
const substituteAt = (input, beginOffset, endOffset, fn, key) => {
  let before = input.slice(0, beginOffset);
  let after = input.slice(endOffset);

  const target = input.slice(beginOffset, endOffset);
  const replaced = fn(target, key);
  return [before, replaced, after];
};

const wrapper = (input, ...opts) => {
  if (!Array.isArray(input)) input = [input];

  return input
    .map((chunk, i) =>
      typeof chunk === "string" && i === 0
        ? substituteAt(chunk, ...opts)
        : chunk
    )
    .flat();
};

// applyReplacements applies a series of string replacements to the input.
// each replacement should consist of a start offset, end offset and
// function that transforms the target string
// Substrings identified by start and end offsets cannot overlap
export const applyReplacements = (input, replacements) =>
  replacements
    .sort(sortFn)
    .reduceRight(
      (accumulator, { start, end, fn }, i) =>
        wrapper(accumulator, start, end, fn, i),
      input
    );

const sortFn = (a, b) => {
  if (a.start > b.start) return 1;
  if (a.start < b.start) return -1;
  return 0;
};
